import { useEffect, useRef, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../shared/hooks/redux';
import { signInWithGoogle, clearError } from '../authSlice';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_AUTH_CLIENT_ID as string;

interface GoogleSignInButtonProps {
  context?: 'signin' | 'signup' | 'use';
  /** Width of the rendered Google button in pixels (max 400) */
  width?: number;
  /** Enable One Tap prompt on this page */
  oneTap?: boolean;
}

/**
 * Generate a cryptographic nonce and its SHA-256 hash.
 * Google gets the hashed version; Supabase gets the raw version.
 */
async function generateNonce(): Promise<[raw: string, hashed: string]> {
  const rawBytes = crypto.getRandomValues(new Uint8Array(32));
  const raw = btoa(String.fromCharCode(...rawBytes));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return [raw, hashed];
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
          prompt: (
            momentListener?: (notification: {
              isNotDisplayed: () => boolean;
              isSkippedMoment: () => boolean;
            }) => void
          ) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

export default function GoogleSignInButton({
  context = 'signin',
  width,
  oneTap = false,
}: GoogleSignInButtonProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { error } = useAppSelector((state) => state.auth);

  const buttonRef = useRef<HTMLDivElement>(null);
  const nonceRef = useRef<{ raw: string; hashed: string } | null>(null);
  const initializedRef = useRef(false);

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      if (error) dispatch(clearError());
      const nonce = nonceRef.current;
      if (!nonce) return;
      try {
        await dispatch(signInWithGoogle({ token: response.credential, nonce: nonce.raw })).unwrap();
        navigate('/curriculum');
      } catch {
        // Error is handled by the slice
      }
    },
    [dispatch, navigate, error]
  );

  // Store the callback in a ref so the GSI callback always sees the latest version
  const callbackRef = useRef(handleCredentialResponse);
  callbackRef.current = handleCredentialResponse;

  useEffect(() => {
    if (initializedRef.current) return;
    if (!GOOGLE_CLIENT_ID) {
      console.error('VITE_GOOGLE_AUTH_CLIENT_ID is not configured');
      return;
    }

    let cancelled = false;

    async function init() {
      // Generate nonce once
      const [raw, hashed] = await generateNonce();
      if (cancelled) return;
      nonceRef.current = { raw, hashed };

      // Load GSI script if not already present
      if (!window.google?.accounts) {
        await new Promise<void>((resolve, reject) => {
          if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
            // Script tag exists but hasn't loaded yet — wait for it
            const check = setInterval(() => {
              if (cancelled) {
                clearInterval(check);
                resolve();
                return;
              }
              if (window.google?.accounts) {
                clearInterval(check);
                resolve();
              }
            }, 50);
            // Give up after 10 seconds
            setTimeout(() => {
              clearInterval(check);
              reject(new Error('Google Sign-In script timed out'));
            }, 10_000);
            return;
          }
          const script = document.createElement('script');
          script.src = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
          document.head.appendChild(script);
        });
      }

      if (cancelled || !window.google?.accounts) return;

      // Initialize — must only be called once per page
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp: { credential: string }) => callbackRef.current(resp),
        nonce: hashed,
        context,
        ux_mode: 'popup',
        auto_select: false,
        itp_support: true,
        use_fedcm_for_button: true,
      });

      // Render the branded button
      if (buttonRef.current) {
        const btnWidth = width ?? buttonRef.current.parentElement?.clientWidth ?? 300;
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: context === 'signup' ? 'signup_with' : 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: String(Math.min(btnWidth, 400)),
        });
      }

      // Optionally show One Tap
      if (oneTap) {
        window.google.accounts.id.prompt();
      }

      initializedRef.current = true;
    }

    init().catch((err) => {
      if (!cancelled) console.error('Google Sign-In init failed:', err);
    });

    return () => {
      cancelled = true;
    };
  }, [context, width, oneTap]);

  if (!GOOGLE_CLIENT_ID) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Google Sign-In is not configured. Set VITE_GOOGLE_AUTH_CLIENT_ID.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <div ref={buttonRef} />
    </Box>
  );
}
